import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSupabase } from '../../contexts/SupabaseContext';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../theme';
import { useTheme } from '@mui/material';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';

// Colors for the pie chart - matching AssetClassDetails
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28DF2',
  '#FF6B6B', '#4ECDC4', '#1A535C', '#FF9F1C', '#011627'
];

const Gold = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-gold', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });

    const sortedData = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
        const bAllocation = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
        return direction === 'asc' 
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      }
      return 0;
    });

    setInvestments(sortedData);
  };

  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch investments');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching gold investments for user:', user.id);

      // Fetch Gold Bonds total
      let goldBondsTotal = 0;
      try {
        const { data: goldBondsData, error: goldBondsError } = await supabase
          .from('gold_bond_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (goldBondsError) {
          console.error('Error fetching gold bonds data:', goldBondsError);
        } else {
          goldBondsTotal = goldBondsData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Gold Bonds total:', goldBondsTotal);
        }
      } catch (err) {
        console.error('Exception fetching gold bonds:', err);
      }

      // Fetch Digital Gold investments
      let digitalGoldTotal = 0;
      try {
        const { data: digitalGoldData, error: digitalGoldError } = await supabase
          .from('digital_gold_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (digitalGoldError) {
          console.error('Error fetching digital gold data:', digitalGoldError);
        } else {
          digitalGoldTotal = digitalGoldData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Digital Gold total:', digitalGoldTotal);
        }
      } catch (err) {
        console.error('Exception fetching digital gold:', err);
      }

      // Fetch Physical Gold investments
      let physicalGoldTotal = 0;
      try {
        const { data: physicalGoldData, error: physicalGoldError } = await supabase
          .from('physical_gold_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (physicalGoldError) {
          console.error('Error fetching physical gold data:', physicalGoldError);
        } else {
          physicalGoldTotal = physicalGoldData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Physical Gold total:', physicalGoldTotal);
        }
      } catch (err) {
        console.error('Exception fetching physical gold:', err);
      }

      // Fetch Gold ETF investments
      let goldETFTotal = 0;
      try {
        const { data: goldETFData, error: goldETFError } = await supabase
          .from('gold_etf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (goldETFError) {
          console.error('Error fetching gold ETF data:', goldETFError);
        } else {
          goldETFTotal = goldETFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Gold ETF total:', goldETFTotal);
        }
      } catch (err) {
        console.error('Exception fetching gold ETF:', err);
      }

      // Fetch Gold Mutual Fund investments
      let goldMFTotal = 0;
      try {
        const { data: goldMFData, error: goldMFError } = await supabase
          .from('gold_mutual_fund_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (goldMFError) {
          console.error('Error fetching gold mutual fund data:', goldMFError);
        } else {
          goldMFTotal = goldMFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Gold Mutual Fund total:', goldMFTotal);
        }
      } catch (err) {
        console.error('Exception fetching gold mutual funds:', err);
      }

      // Format data for display
      const investmentsList = [
        {
          id: 'gold-bonds',
          name: 'Gold Bonds',
          value: goldBondsTotal,
          path: '/investments/gold/gold-bonds'
        },
        {
          id: 'digital-gold',
          name: 'Digital Gold',
          value: digitalGoldTotal,
          path: '/investments/gold/digital-gold'
        },
        {
          id: 'physical-gold',
          name: 'Physical Gold',
          value: physicalGoldTotal,
          path: '/investments/gold/physical-gold'
        },
        {
          id: 'gold-etf',
          name: 'Gold ETF',
          value: goldETFTotal,
          path: '/investments/gold/gold-etf'
        },
        {
          id: 'gold-mutual-fund',
          name: 'Mutual Fund',
          value: goldMFTotal,
          path: '/investments/gold/gold-mutual-fund'
        }
      ];

      // Sort by value (descending)
      investmentsList.sort((a, b) => b.value - a.value);
      
      setInvestments(investmentsList);
      const total = investmentsList.reduce((sum, item) => sum + item.value, 0);
      setTotalValue(total);
      console.log('Total value of all gold investments:', total);

    } catch (error) {
      console.error('Error in fetchInvestments:', error);
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header with back button */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={() => navigate('/investments')}
          sx={{
            mr: 2,
            color: theme.palette.grey[500],
            '&:hover': { color: theme.palette.primary.main }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box data-tour="inv-class-header">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Gold Investments
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Total: {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              maximumFractionDigits: 0
            }).format(totalValue)}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <TourButton onClick={tour.start} />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left side - Table */}
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-class-products"
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backdropFilter: 'blur(20px)',
                boxShadow: 'var(--fx-card-shadow)'
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Products
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Product Name
                        {sortConfig.key === 'name' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('value')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Value
                        {sortConfig.key === 'value' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('allocation')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Allocation
                        {sortConfig.key === 'allocation' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => (
                      <TableRow
                        key={investment.id}
                        onClick={() => navigate(investment.path)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: COLORS[index % COLORS.length],
                                mr: 2
                              }}
                            />
                            {investment.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                          {totalValue > 0 ? ((investment.value / totalValue) * 100).toFixed(1) : '0'}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Add Total Row */}
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Distribution Chart */}
          <Grid item xs={12} md={4}>
            <Paper
              data-tour="inv-class-chart" sx={{ 
              height: '100%', 
              borderRadius: 3, 
              p: 2,
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Distribution</Typography>
              {investments.length > 0 && totalValue > 0 ? (
                <PieChart colors={chartPalette}
                  series={[{
                    data: investments.map(item => ({
                      id: item.id,
                      value: item.value,
                      label: item.name
                    })),
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    faded: { innerRadius: 30, additionalRadius: -30 },
                    label: false,
                    labelLine: false,
                  }]}
                  height={350}
                  legend={{
                    hidden: true
                  }}
                  margin={{
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0
                  }}
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary' 
                }}>
                  <Typography>No gold investments found</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
      <GuidedTour steps={TOURS['inv-gold']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default Gold;